import { formatOverwatchValue } from './format';
import {
  IOverwatchEnum,
  IOverwatchEnvelope,
  IOverwatchLink,
  IOverwatchPoint,
  IOverwatchRecord,
  OverwatchGranularity,
  OverwatchUnit
} from './types';

/** `y: null` is a gap: NaN breaks the line or drops the bar, and is never drawn as zero. */
export const seriesReadings = (points: IOverwatchPoint[]): number[] =>
  points.map(point =>
    typeof point?.y === 'number' && Number.isFinite(point.y) ? point.y : Number.NaN
  );

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** An axis caption for one bucket, chosen by the series granularity. */
export const bucketLabel = (x: string, granularity?: OverwatchGranularity): string => {
  if (granularity === 'hour') {
    const match = /T(\d{2})/.exec(x);

    return match ? `${match[1]}:00` : x;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(x);

  if (!match) return x;

  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1] ?? month;

  switch (granularity) {
    case 'quarter':
      return `Q${Math.floor((Number(month) - 1) / 3) + 1} ${year}`;
    case 'month':
      return `${monthName} ${year}`;
    default:
      return `${Number(day)} ${monthName}`;
  }
};

export const seriesLabels = (
  points: IOverwatchPoint[],
  granularity?: OverwatchGranularity
): string[] => points.map(point => bucketLabel(point.x, granularity));

export interface IOverwatchCollectionPage {
  items: IOverwatchRecord[];
  nextCursor: string | null;
  totalCount: number | null;
  hasMore: boolean;
}

export const EMPTY_COLLECTION: IOverwatchCollectionPage = {
  items: [],
  nextCursor: null,
  totalCount: null,
  hasMore: false
};

/** The first page of a collection envelope; only records carrying an `id` are kept. */
export const collectionPage = (
  envelope: IOverwatchEnvelope | undefined | null,
  data: unknown
): IOverwatchCollectionPage => {
  if (!Array.isArray(data)) return EMPTY_COLLECTION;

  const items = data.filter(
    (item): item is IOverwatchRecord =>
      !!item && typeof item === 'object' && (item as IOverwatchRecord).id !== undefined
  );
  const nextCursor = typeof envelope?.next_cursor === 'string' ? envelope.next_cursor : null;

  return {
    items,
    nextCursor,
    totalCount: typeof envelope?.total_count === 'number' ? envelope.total_count : null,
    hasMore: nextCursor !== null
  };
};

const isEnum = (value: unknown): value is IOverwatchEnum =>
  !!value && typeof value === 'object' && typeof (value as IOverwatchEnum).label === 'string';

/** A text field, or an enum field's label. */
export const fieldText = (record: IOverwatchRecord, field: string): string | null => {
  const value = record[field];

  if (typeof value === 'string' && value.trim()) return value;

  return isEnum(value) ? value.label : null;
};

export const fieldCode = (record: IOverwatchRecord, field: string): string | null => {
  const value = record[field];

  return isEnum(value) && typeof value.code === 'string' ? value.code : null;
};

export const fieldNumber = (record: IOverwatchRecord, field: string): number | null => {
  const value = record[field];

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const fieldFormatted = (
  record: IOverwatchRecord,
  field: string,
  unit: OverwatchUnit
): string | null => {
  const value = fieldNumber(record, field);

  return value === null ? null : formatOverwatchValue(value, unit);
};

export const asLink = (value: unknown): IOverwatchLink | null =>
  value &&
  typeof value === 'object' &&
  typeof (value as IOverwatchLink).url === 'string' &&
  /^https:\/\//.test((value as IOverwatchLink).url)
    ? (value as IOverwatchLink)
    : null;

export const fieldLink = (record: IOverwatchRecord, field: string): IOverwatchLink | null =>
  asLink(record[field]);

/** "12 Oct 2026" from a `date` or `datetime`. */
export const formatOverwatchDate = (value: string | null | undefined): string | null => {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;

  if (!match) return null;

  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

/** "42 mins ago" / "In 3 days" from a `datetime`, against `now`. */
export const relativeTime = (value: string | null | undefined, now: number = Date.now()) => {
  const time = value ? Date.parse(value) : Number.NaN;

  if (!Number.isFinite(time)) return null;

  const diff = time - now;
  const minutes = Math.round(Math.abs(diff) / 60000);
  const [amount, unit] =
    minutes < 60
      ? [Math.max(minutes, 1), 'min']
      : minutes < 60 * 24
      ? [Math.round(minutes / 60), 'hour']
      : [Math.round(minutes / (60 * 24)), 'day'];
  const text = `${amount} ${unit}${amount === 1 ? '' : 's'}`;

  return diff > 0 ? `In ${text}` : `${text} ago`;
};
